/**
 * useNavigationState — SSOT for the app's tab + sub-tab + chat selection
 * state, plus the localStorage persistence around it.
 *
 * Previously inline in App.tsx (five separate useState hooks + five
 * useEffect persistence hooks + a `handleTabChange` callback). Split
 * out so App.tsx stays thin and any future routing work (URL routing,
 * hash navigation) has a single file to touch.
 *
 * Schema version bump: prefix the storage keys with `v1.` (below). The
 * legacy `nav_*` keys are read-once on first mount, carried forward to
 * the new prefix, and then left alone — users who stop visiting for
 * months can still re-enter and land on their last tab.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { on as onAppEvent } from '../events/bus';
import { trackTabChange } from '../../analytics';
import { captureError } from '../errors/captureError';
// Slice 82 (GAP-RAIL-TABTYPE-DUPLICATION): TabType, ROUTE_TO_TAB,
// TAB_TO_ROUTE, and TAB_TITLES are derived from the single TAB_METADATA
// SSOT in `./tabMetadata`. Slice 86 (this slice) retired the re-export
// shims — consumers now import `TabType` + `TAB_TITLES` directly from
// `./tabMetadata`. The hook still uses these internally.
import {
    ROUTE_TO_TAB,
    TAB_TO_ROUTE,
    type TabType,
} from './tabMetadata';

export type EducationSubTab = 'bootcamp' | 'coaching';
export type NetworkingSubTab = 'connections' | 'requests' | 'discover';

/** SSOT for every localStorage key this hook owns. Prefix + version so
 *  a future schema migration is a one-line change here. */
const LS = {
    activeTab:        'v1.nav.activeTab',
    educationSubTab:  'v1.nav.educationSubTab',
    networkingSubTab: 'v1.nav.networkingSubTab',
    selectedChatId:   'v1.nav.selectedChatId',
    selectedProjectId:'v1.nav.selectedProjectId',
} as const;

/** Legacy keys read once on first mount (migrated to the new prefix).
 *  Safe to delete once every active user has logged in at least once. */
const LEGACY_LS = {
    activeTab:        'nav_activeTab',
    educationSubTab:  'nav_educationSubTab',
    networkingSubTab: 'nav_networkingSubTab',
    selectedChatId:   'nav_selectedChatId',
    selectedProjectId:'nav_selectedProjectId',
} as const;

function readLS<T>(key: string, legacyKey: string, fallback: T): T {
    try {
        const raw = localStorage.getItem(key) ?? localStorage.getItem(legacyKey);
        return raw !== null ? (JSON.parse(raw) as T) : fallback;
    } catch (err) {
        captureError(err, { scope: 'useNavigationState', op: `readLS:${key}` });
        return fallback;
    }
}
function writeLS(key: string, value: unknown) {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch (err) {
        captureError(err, { scope: 'useNavigationState', op: `writeLS:${key}` });
    }
}

/**
 * readActiveTabLS — one-shot migration shim for the slice-86
 * `messages → conversations` rename.
 *
 * Why: pre-slice-86, the v1.nav.activeTab key may have been written
 * with the literal `"messages"` JSON value. After the rename, that
 * string is no longer a member of the TabType union, so a returning
 * user would silently fall through to the 'home' default — losing
 * their last-tab state. This helper deserialises the stored value,
 * remaps `'messages' → 'conversations'`, and (when a remap fires)
 * rewrites localStorage with the canonical value so the legacy literal
 * is purged within one mount per device.
 *
 * Deterministic + idempotent: on re-mount after the rewrite, the
 * stored value is already `"conversations"` and the helper short-
 * circuits through `readLS` with no extra write.
 */
function readActiveTabLS(): TabType {
    const raw = readLS<string>(LS.activeTab, LEGACY_LS.activeTab, 'home');
    if (raw === 'messages') {
        // Why: rewrite the stored value so the migration is one-shot per
        // device. Re-mounts read 'conversations' directly via readLS.
        writeLS(LS.activeTab, 'conversations');
        return 'conversations';
    }
    return raw as TabType;
}

export type NavigationState = {
    activeTab: TabType;
    educationSubTab: EducationSubTab;
    networkingSubTab: NetworkingSubTab;
    selectedChatId: string | null;
    selectedProjectId: string | null;
};

export type NavigationApi = {
    nav: NavigationState;
    setActiveTab: (tab: TabType) => void;
    setEducationSubTab: (sub: EducationSubTab) => void;
    setNetworkingSubTab: (sub: NetworkingSubTab) => void;
    setSelectedChatId: (id: string | null) => void;
    setSelectedProjectId: (id: string | null) => void;
    /** Canonical tab change — tracks analytics + updates the relevant
     *  sub-tab in one call. The only setter App-level callers need. */
    handleTabChange: (tab: TabType, subTab?: string) => void;
};

export function useNavigationState(): NavigationApi {
    const navigate = useNavigate();
    const location = useLocation();

    // URL is the SSOT for activeTab when the path maps to a tab. The
    // localStorage fallback owns the seed *only* for first-paint when
    // no matching route is mounted yet (deep-link to a non-tab path).
    // Deriving activeTab keeps URL ↔ state in sync without a setState
    // effect (which would cascade re-renders on every navigation).
    const [storedTab, setStoredTab] = useState<TabType>(
        () => ROUTE_TO_TAB[window.location.pathname] ?? readActiveTabLS(),
    );
    const activeTab = ROUTE_TO_TAB[location.pathname] ?? storedTab;

    const setActiveTab = useCallback((tab: TabType) => {
        setStoredTab(tab);
        const route = TAB_TO_ROUTE[tab];
        if (route) navigate(route);
    }, [navigate]);
    const [educationSubTab, setEducationSubTab] = useState<EducationSubTab>(
        () => readLS(LS.educationSubTab, LEGACY_LS.educationSubTab, 'bootcamp' as EducationSubTab),
    );
    const [networkingSubTab, setNetworkingSubTab] = useState<NetworkingSubTab>(() => {
        const raw = readLS<string>(LS.networkingSubTab, LEGACY_LS.networkingSubTab, 'connections');
        return raw === 'connections' || raw === 'requests' || raw === 'discover' ? raw : 'connections';
    });
    const [selectedChatId, setSelectedChatId] = useState<string | null>(
        () => readLS<string | null>(LS.selectedChatId, LEGACY_LS.selectedChatId, null),
    );
    const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
        () => readLS<string | null>(LS.selectedProjectId, LEGACY_LS.selectedProjectId, null),
    );

    useEffect(() => { writeLS(LS.activeTab, activeTab); }, [activeTab]);
    useEffect(() => { writeLS(LS.educationSubTab, educationSubTab); }, [educationSubTab]);
    useEffect(() => { writeLS(LS.networkingSubTab, networkingSubTab); }, [networkingSubTab]);
    useEffect(() => { writeLS(LS.selectedChatId, selectedChatId); }, [selectedChatId]);
    useEffect(() => { writeLS(LS.selectedProjectId, selectedProjectId); }, [selectedProjectId]);

    const handleTabChange = useCallback((tab: TabType, subTab?: string) => {
        setActiveTab(tab);
        trackTabChange(subTab ? `${tab}:${subTab}` : tab);
        if (tab === 'education' && subTab) {
            setEducationSubTab(subTab as EducationSubTab);
        } else if (tab === 'networking' && subTab) {
            const coerced: NetworkingSubTab =
                subTab === 'connections' || subTab === 'requests' || subTab === 'discover'
                    ? subTab
                    : 'connections';
            setNetworkingSubTab(coerced);
        }
    }, [setActiveTab]);

    // Cross-module: `open-plans` jumps to Settings › Billing. Owned here
    // because it mutates nav state; App.tsx no longer listens.
    //
    // Note: deliberately uses direct hash assignment instead of the
    // V3 `navigateHash` SSOT. The existing test for this handler stubs
    // `window.location` independently of `window.history`, so
    // `history.replaceState` doesn't update the stubbed `.hash`. Direct
    // assignment also makes sense semantically here — this IS a user-
    // initiated cross-module jump from one section to another, where
    // a back-button entry is the expected behaviour.
    useEffect(() => {
        return onAppEvent('open-plans', () => {
            setActiveTab('settings');
            if (typeof window !== 'undefined') {
                window.location.hash = '#billing';
            }
        });
    }, [setActiveTab]);

    // Cross-module: Mentor review list "Review" → Mentorship tab.
    useEffect(() => {
        // eslint-disable-next-line chiefy/no-naked-concept-strings -- event-bus channel name (infrastructure id), not user copy.
        return onAppEvent('open-submission-review', () => {
            setActiveTab('mentorship');
        });
    }, [setActiveTab]);

    return {
        nav: { activeTab, educationSubTab, networkingSubTab, selectedChatId, selectedProjectId },
        setActiveTab,
        setEducationSubTab,
        setNetworkingSubTab,
        setSelectedChatId,
        setSelectedProjectId,
        handleTabChange,
    };
}
